import {
  Badge,
  Button,
  Flex,
  Heading,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import moment from "moment";
import { useEffect, useState } from "react";
import { FaBullhorn, FaChevronUp, FaComment, FaComments, FaQuestion } from "react-icons/fa";
import {
  BseDto,
  BseQuestionDto,
  BseQuestionStatus,
  NegotiationStateName,
  useCreateBseAnnouncementMutation,
  useCreateBseQuestionAsBuyerMutation,
  useCreateBseQuestionCommentAsBuyerMutation,
  useUpdateBasicSourcingEventMutation,
  useUpdateBseQuestionMutation,
} from "../../../../autogen/bff-api";
import { ConversationModal } from "../../../../common/ConversationModal";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { setSourcingEvent } from "../../../../common/redux/reducers/basicSourcingEventReducer";
import { useQueryParams } from "../../../../common/useQueryParams";
import { Announcements } from "./announcements/Announcements";
import { AnnouncementData } from "./announcements/NewAnnouncementModal";
import { useNegotiation } from "./NegotiationContext";
import { SupplierQuestions } from "./questions/SupplierQuestions";

export const EventDialog = ({ event }: { event: BseDto }) => {
  const dispatch = useAppDispatch();
  const params = useQueryParams();

  const [editEvent, { isLoading: isLoadingEditEvent }] = useUpdateBasicSourcingEventMutation();
  const [createQuestionAsBuyer] = useCreateBseQuestionAsBuyerMutation();
  const [createQuestionCommentAsBuyer] = useCreateBseQuestionCommentAsBuyerMutation();
  const [updateBseQuestion] = useUpdateBseQuestionMutation();
  const [createBseAnnouncement] = useCreateBseAnnouncementMutation();

  const [showDialog, setShowDialog] = useState(false);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [isSupplierQuestionsOpen, setIsSupplierQuestionsOpen] = useState(false);
  const [isAnnouncementsOpen, setIsAnnouncementsOpen] = useState(false);
  const { currentRound, event: negotiationEvent } = useNegotiation();

  const tabParam = params.get("tab");

  useEffect(() => {
    if (tabParam === "qa" && !isSupplierQuestionsOpen) {
      setIsSupplierQuestionsOpen(true);
    } else if (tabParam === "announcements" && !isAnnouncementsOpen) {
      setIsAnnouncementsOpen(true);
    } else if (!tabParam) {
      setIsSupplierQuestionsOpen(false);
      setIsAnnouncementsOpen(false);
    }
  }, [tabParam, isSupplierQuestionsOpen, isAnnouncementsOpen]);

  const allQuestions = [
    ...(event.questions ?? []),
    ...(negotiationEvent.negotiationContext?.rounds.flatMap((r) => r.questions ?? []) ?? []),
  ];
  const unresolvedQuestionsCount = allQuestions.filter((q) => q.status === "Unresolved").length;

  const [initialAnnouncementValues, setInitialAnnouncementValues] = useState<AnnouncementData | null>(null);

  if (!event) return null;

  return (
    <>
      <ConversationModal
        canComment={event.canEdit}
        isOpen={isConversationModalOpen}
        onClose={() => setIsConversationModalOpen(false)}
        conversation={event.internalConversation.map((msg) => ({
          id: msg.id,
          value: msg.value,
          createdBy: `${msg.createdBy?.firstName ?? ""} ${msg.createdBy?.lastName ?? ""}`,
          createdAt: moment(msg.createdAt).format("DD MMM YY hh.mm"),
        }))}
        handleMessage={async (msg: string) => {
          const updatedEvent = await editEvent({
            eventId: event.id,
            editSourcingEventRequest: { addInternalMessage: msg },
          }).unwrap();
          dispatch(setSourcingEvent(updatedEvent));
        }}
        isLoading={isLoadingEditEvent}
      />
      <Modal isOpen={isSupplierQuestionsOpen} onClose={() => params.clearBseDialogTab()} size={"3xl"}>
        <ModalOverlay />
        <ModalContent
          width="full"
          height="80vh"
          maxHeight="80vh"
          overflow="visible"
          display="flex"
          flexDirection="column"
          px="5"
          pb="5"
        >
          <ModalCloseButton />
          <ModalBody flex="1" minHeight="0" overflowY="auto" style={{ scrollbarGutter: "stable both-edges" }}>
            <Flex height={"full"} flexDirection={"column"}>
              <Heading as="h2" size="md" py="5">
                {t("Supplier Dialog")}
              </Heading>
              <SupplierQuestions
                data={event}
                canManageQuestions={event.canEdit}
                canCreateNewQuestion={currentRound ? currentRound.stateName !== NegotiationStateName.Draft : true}
                newQuestionDisabledReason={
                  currentRound && currentRound.stateName === NegotiationStateName.Draft
                    ? String(t("You can't ask questions while the negotiation round is a draft"))
                    : undefined
                }
                addComment={async (values: {
                  comment: string;
                  question: BseQuestionDto;
                  negotiationRoundId?: string;
                  createAnnouncementDraft: boolean;
                }) => {
                  const result = await createQuestionCommentAsBuyer({
                    eventId: event.id,
                    questionId: values.question.id,
                    createBseQuestionCommentRequest: {
                      content: values.comment,
                      negotiationRoundId: values.negotiationRoundId,
                    },
                  }).unwrap();
                  if (values.createAnnouncementDraft) {
                    setInitialAnnouncementValues({
                      title: values.question.subject,
                      content: values.comment,
                    });
                    params.setCreateAnnouncement();
                  }
                  dispatch(setSourcingEvent(result));
                }}
                changeStatus={async (status: BseQuestionStatus, questionId: string, negotiationRoundId?: string) => {
                  return updateBseQuestion({
                    eventId: event.id,
                    questionId,
                    updateBseQuestionRequest: { status, negotiationRoundId },
                  })
                    .unwrap()
                    .then(() => true)
                    .catch(() => false);
                }}
                announcements={event.announcements}
                organizationInvites={event.organizationInvites}
                createNewQuestion={({ subject, content, askedToOrganizationId }) =>
                  createQuestionAsBuyer({
                    eventId: event.id,
                    createBseQuestionRequest: {
                      subject,
                      content,
                      askedToOrganizationId,
                      negotiationRoundId: currentRound?.id,
                    },
                  })
                    .unwrap()
                    .then((result) => {
                      dispatch(setSourcingEvent(result));
                      return result;
                    })
                }
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isAnnouncementsOpen} onClose={() => params.clearBseDialogTab()} size={"3xl"}>
        <ModalOverlay />
        <ModalContent width="full" maxH="80vh" overflow="hidden" display="flex" flexDirection="column" padding="5">
          <ModalCloseButton />
          <ModalBody flex="1" minH="0" overflowY="auto" style={{ scrollbarGutter: "stable both-edges" }}>
            <Flex height={"full"} flexDirection={"column"}>
              <Heading as="h2" size="md" pb="2">
                {t("Announcements")}
              </Heading>
              <Announcements
                canCreateAnnouncement={event.canEdit}
                announcements={event.announcements}
                initialAnnouncementValues={initialAnnouncementValues}
                createAnnouncement={async (values: { title: string; content: string }) => {
                  const result = await createBseAnnouncement({
                    eventId: event.id,
                    createBseAnnouncementRequest: { title: values.title, content: values.content },
                  }).unwrap();
                  setInitialAnnouncementValues(null);
                  dispatch(setSourcingEvent(result));
                  return true;
                }}
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Flex flexDirection="column" position="fixed" bottom="0" left="0" zIndex="10">
        <Menu size="lg" colorScheme="cyan" onOpen={() => setShowDialog(true)} onClose={() => setShowDialog(false)}>
          <MenuButton
            as={Button}
            position="relative"
            size="lg"
            roundedBottom="0"
            roundedLeft="0"
            border="1px solid"
            borderColor="smBorder"
            backgroundColor="smBackground"
            _hover={{ backgroundColor: "smBackgroundSecondary" }}
            shadow="lg"
            leftIcon={<Icon as={FaComments} />}
            rightIcon={<Icon boxSize="3" as={FaChevronUp} />}
          >
            {t("Event dialog")}{" "}
            {unresolvedQuestionsCount > 0 && !showDialog && (
              <Badge position="absolute" top="-2" right="-2" colorScheme="red" variant="solid" rounded="md">
                {unresolvedQuestionsCount} {t("unresolved!")}
              </Badge>
            )}
          </MenuButton>
          <MenuList>
            <MenuItem icon={<Icon as={FaComment} />} onClick={() => setIsConversationModalOpen(true)}>
              {t("Internal conversation")}
            </MenuItem>
            <MenuItem icon={<Icon as={FaQuestion} />} onClick={() => params.clearSelectedQuestion()}>
              <Flex alignItems="center">
                <Text>{t("Supplier Dialog")}</Text>
                {unresolvedQuestionsCount > 0 && (
                  <Badge colorScheme="red" variant="solid" rounded="md" ml="3">
                    {unresolvedQuestionsCount} {t("unresolved!")}
                  </Badge>
                )}
              </Flex>
            </MenuItem>
            <MenuItem icon={<Icon as={FaBullhorn} />} onClick={() => params.clearSelectedAnnouncement()}>
              {t("Announcements")}
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </>
  );
};
